import React, { useState, useMemo } from "react";
import { Form, Row, Col, Alert, message } from "antd";
import { useDispatch, useSelector } from "react-redux";

import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomInput from "../../../components/CustomInput/Input";
import InputWithButton from "../../../components/CustomInput/InputWithButton";
import CustomButton from "../../../components/CustomButton/Button";

import {
  companyNameRules,
  nameRules,
  phoneNumberRules,
  emailRules,
  panRules,
  gstinRules,
  ifscRules,
  accNumberRules,
} from "../../../helpers/helpers";

import {
  createSupplier,
  verifyIfsc,
  verifySupplierPan,
  verifySupplierGstin,
  verifySupplierBank,
} from "../../../redux/slices/supplierSlice";

export const normalizeEmail = (email) => (email || "").toLowerCase().trim();

export const normalizePhone = (phone) => {
  if (!phone) return "";
  const cleaned = String(phone).replace(/\D/g, "");
  return cleaned.length > 10 ? cleaned.slice(-10) : cleaned;
};

export const extractBusinessEmails = (businessData, user) => {
  const emails = new Set();
  const list = [
    businessData?.email,
    businessData?.workEmail,
    businessData?.businessEmail,
    businessData?.userDetails?.email,
    businessData?.busDetails?.email,
    user?.email,
    user?.workEmail,
    user?.businessEmail,
  ];
  list.forEach((e) => {
    if (e && typeof e === "string") {
      emails.add(normalizeEmail(e));
    }
  });
  return Array.from(emails);
};

export const extractBusinessPhones = (businessData, user) => {
  const phones = new Set();
  const list = [
    businessData?.phoneNumber,
    businessData?.mobile,
    businessData?.contactPhoneNumber,
    businessData?.workPhone,
    businessData?.userDetails?.phoneNumber,
    businessData?.userDetails?.mobile,
    businessData?.busDetails?.phoneNumber,
    businessData?.busDetails?.contactPhoneNumber,
    user?.phoneNumber,
    user?.mobile,
    user?.contactPhoneNumber,
    user?.workPhone,
  ];
  list.forEach((p) => {
    if (p) {
      const clean = normalizePhone(p);
      if (clean && clean.length === 10) {
        phones.add(clean);
      }
    }
  });
  return Array.from(phones);
};

export const duplicateEmailValidator = (suppliersList, businessEmails = []) => (_, value) => {
  if (!value) return Promise.resolve();
  const cleanEmail = normalizeEmail(value);

  const bEmails = (Array.isArray(businessEmails) ? businessEmails : [businessEmails])
    .map(normalizeEmail)
    .filter(Boolean);

  if (bEmails.includes(cleanEmail)) {
    return Promise.reject(
      new Error(
        "Supplier email address cannot be the same as the registered business email address."
      )
    );
  }

  const isDuplicate = (suppliersList || []).some(
    (s) => normalizeEmail(s.pocEmail) === cleanEmail
  );
  if (isDuplicate) {
    return Promise.reject(
      new Error("Email address already exists for another supplier.")
    );
  }
  return Promise.resolve();
};

export const duplicatePhoneValidator = (suppliersList, businessPhones = []) => (_, value) => {
  if (!value) return Promise.resolve();
  const cleanMobile = normalizePhone(value);
  if (cleanMobile.length === 10) {
    const bPhones = (Array.isArray(businessPhones) ? businessPhones : [businessPhones])
      .map(normalizePhone)
      .filter((p) => p && p.length === 10);

    if (bPhones.includes(cleanMobile)) {
      return Promise.reject(
        new Error(
          "Supplier mobile number cannot be the same as the registered business mobile number."
        )
      );
    }

    const isDuplicate = (suppliersList || []).some(
      (s) => normalizePhone(s.pocMobile) === cleanMobile
    );
    if (isDuplicate) {
      return Promise.reject(
        new Error("Phone number already exists for another supplier.")
      );
    }
  }
  return Promise.resolve();
};

const NewSupplierModal = ({
  open,
  onClose,
  onSuccess,
  existingSuppliers,
  businessDetails,
  businessEmail,
  businessMobile,
  businessEmails: propBusinessEmails,
  businessPhones: propBusinessPhones,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const { submitLoading, submitError, suppliersList = [] } = useSelector(
    (state) => state.supplier
  );
  const user = useSelector((state) => state.auth?.user);
  const busDetailedViewData = useSelector(
    (state) => state.profile1?.busDetailedViewData
  );

  const suppliers = useMemo(
    () => existingSuppliers || suppliersList || [],
    [existingSuppliers, suppliersList]
  );

  const registeredBusinessEmails = useMemo(() => {
    const extracted = extractBusinessEmails(
      businessDetails || busDetailedViewData,
      user
    );
    if (businessEmail) extracted.push(normalizeEmail(businessEmail));
    if (propBusinessEmails) {
      (Array.isArray(propBusinessEmails)
        ? propBusinessEmails
        : [propBusinessEmails]
      ).forEach((e) => {
        if (e) extracted.push(normalizeEmail(e));
      });
    }
    return Array.from(new Set(extracted.filter(Boolean)));
  }, [
    businessDetails,
    busDetailedViewData,
    user,
    businessEmail,
    propBusinessEmails,
  ]);

  const registeredBusinessPhones = useMemo(() => {
    const extracted = extractBusinessPhones(
      businessDetails || busDetailedViewData,
      user
    );
    if (businessMobile) extracted.push(normalizePhone(businessMobile));
    if (propBusinessPhones) {
      (Array.isArray(propBusinessPhones)
        ? propBusinessPhones
        : [propBusinessPhones]
      ).forEach((p) => {
        if (p) extracted.push(normalizePhone(p));
      });
    }
    return Array.from(
      new Set(extracted.filter((p) => p && p.length === 10))
    );
  }, [
    businessDetails,
    busDetailedViewData,
    user,
    businessMobile,
    propBusinessPhones,
  ]);

  const emailValidationRules = useMemo(
    () => [
      ...emailRules("POC"),
      {
        validator: duplicateEmailValidator(suppliers, registeredBusinessEmails),
        validateTrigger: ["onBlur", "onChange"],
      },
    ],
    [suppliers, registeredBusinessEmails]
  );

  const phoneValidationRules = useMemo(
    () => [
      ...phoneNumberRules("POC"),
      {
        validator: duplicatePhoneValidator(suppliers, registeredBusinessPhones),
        validateTrigger: ["onBlur", "onChange"],
      },
    ],
    [suppliers, registeredBusinessPhones]
  );

  const [ifscVerifying, setIfscVerifying] = useState(false);

  // Verification states
  const [isPanVerified, setIsPanVerified] = useState(false);
  const [panVerifying, setPanVerifying] = useState(false);

  const [isGstinVerified, setIsGstinVerified] = useState(false);
  const [gstinVerifying, setGstinVerifying] = useState(false);

  const [isBankVerified, setIsBankVerified] = useState(false);
  const [bankVerifying, setBankVerifying] = useState(false);
  const [bankVerificationId, setBankVerificationId] = useState(null);

  // All required verification checks (PAN, GSTIN, Bank Account) must pass before enabling Add Supplier
  const isAllVerified = isPanVerified && isGstinVerified && isBankVerified;

  // Form field watchers for button enable/disable
  const panValue = Form.useWatch("pan", form);
  const gstinValue = Form.useWatch("gstin", form);
  const accNumValue = Form.useWatch("accNum", form);
  const ifscValue = Form.useWatch("ifsc", form);
  const accHolderNameValue = Form.useWatch("accHolderName", form);

  /**
   * Helper function to check whether entered supplier name matches PAN name
   */
  const checkNameMatch = (enteredName, panName) => {
    if (!enteredName || !panName) return false;
    const clean1 = enteredName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const clean2 = panName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (clean1 === clean2) return true;

    const compact1 = clean1.replace(/\s+/g, "");
    const compact2 = clean2.replace(/\s+/g, "");
    if (
      compact1 === compact2 ||
      compact1.includes(compact2) ||
      compact2.includes(compact1)
    ) {
      return true;
    }

    const words1 = clean1.split(" ").filter((w) => w.length > 1);
    const words2 = clean2.split(" ").filter((w) => w.length > 1);
    const matchingWords = words1.filter((w) => words2.includes(w));
    if (matchingWords.length >= Math.min(words1.length, words2.length) * 0.6) {
      return true;
    }

    return false;
  };

  /**
   * PAN Verification Handler
   */
  const handleVerifyPan = async () => {
    const pan = form.getFieldValue("pan");
    if (!pan || !/[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan.toUpperCase().trim())) {
      message.error("Please enter a valid 10-digit PAN number");
      return;
    }

    const enteredSupplierName = form.getFieldValue("supplierName")?.trim();
    if (!enteredSupplierName) {
      message.error("Please enter Supplier Name before verifying PAN");
      return;
    }

    setPanVerifying(true);
    try {
      const res = await dispatch(
        verifySupplierPan({
          pan: pan.toUpperCase().trim(),
          name: enteredSupplierName,
        })
      );

      if (res?.type?.endsWith("/rejected") || res?.error) {
        const errMsg =
          typeof res?.payload === "string"
            ? res.payload
            : res?.payload?.error ||
            res?.payload?.message ||
            "PAN verification failed.";
        message.error(errMsg);
        setIsPanVerified(false);
        return;
      }

      const verifiedPanName =
        res?.payload?.response?.panName ||
        res?.payload?.panName ||
        res?.payload?.data?.panName ||
        res?.payload?.data?.response?.panName ||
        "";

      if (verifiedPanName && !checkNameMatch(enteredSupplierName, verifiedPanName)) {
        message.error(
          `Supplier Name does not match name on PAN (${verifiedPanName})`
        );
        setIsPanVerified(false);
        return;
      }

      form.setFieldsValue({
        supplierName: verifiedPanName || enteredSupplierName,
        accHolderName: verifiedPanName || enteredSupplierName,
      });

      setIsPanVerified(true);
      message.success("PAN verified successfully.");
    } catch (e) {
      setIsPanVerified(false);
      message.error("PAN verification failed.");
    } finally {
      setPanVerifying(false);
    }
  };

  /**
   * GSTIN Verification Handler
   */
  const handleVerifyGstin = async () => {
    const gstin = form.getFieldValue("gstin");
    if (
      !gstin ||
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        gstin.toUpperCase().trim()
      )
    ) {
      message.error("Please enter a valid 15-digit GSTIN number");
      return;
    }

    const pan = form.getFieldValue("pan");
    setGstinVerifying(true);
    try {
      const res = await dispatch(
        verifySupplierGstin({
          gstin: gstin.toUpperCase().trim(),
          pan: pan ? pan.toUpperCase().trim() : undefined,
          name: form.getFieldValue("supplierName")?.trim() || undefined,
        })
      );

      if (res?.type?.endsWith("/rejected") || res?.error) {
        const errMsg =
          typeof res?.payload === "string"
            ? res.payload
            : res?.payload?.error ||
            res?.payload?.message ||
            "GSTIN verification failed.";
        message.error(errMsg);
        setIsGstinVerified(false);
        return;
      }

      setIsGstinVerified(true);
      message.success("GSTIN verified successfully.");
    } catch (e) {
      setIsGstinVerified(false);
      message.error("GSTIN verification failed.");
    } finally {
      setGstinVerifying(false);
    }
  };

  /**
   * Bank Account Verification Handler
   */
  const handleVerifyBank = async () => {
    const accNum = form.getFieldValue("accNum");
    const ifsc = form.getFieldValue("ifsc");
    const accHolderName = form.getFieldValue("accHolderName");

    if (!accNum || accNum.length < 8 || accNum.length > 18) {
      message.error("Please enter a valid Bank Account Number (8 to 18 digits)");
      return;
    }
    if (!ifsc || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc.toUpperCase().trim())) {
      message.error("Please enter a valid IFSC code");
      return;
    }
    if (!accHolderName || !accHolderName.trim()) {
      message.error("Please enter Account Holder Name before verifying");
      return;
    }

    setBankVerifying(true);
    try {
      const res = await dispatch(
        verifySupplierBank({
          ifsc: ifsc.toUpperCase().trim(),
          accNum: accNum.trim(),
          accHolderName: accHolderName.trim(),
        })
      );

      if (res?.type?.endsWith("/rejected") || res?.error) {
        const errMsg =
          typeof res?.payload === "string"
            ? res.payload
            : res?.payload?.error ||
            res?.payload?.message ||
            "Bank account verification failed.";
        message.error(errMsg);
        setIsBankVerified(false);
        setBankVerificationId(null);
        return;
      }

      setIsBankVerified(true);
      const vId =
        res?.payload?.response?.verificationId ||
        res?.payload?.verificationId ||
        res?.payload?.data?.verificationId ||
        null;
      setBankVerificationId(vId);
      message.success("Bank account verified successfully.");
    } catch (e) {
      setIsBankVerified(false);
      setBankVerificationId(null);
      message.error("Bank account verification failed.");
    } finally {
      setBankVerifying(false);
    }
  };

  /**
   * Handle IFSC change
   * When valid IFSC is entered, call API
   * and automatically populate Bank Name and Branch Name.
   */
  const handleIfscChange = async (e) => {
    const val = (e.target.value || "").toUpperCase().trim();

    // Update IFSC value
    form.setFieldsValue({
      ifsc: val,
      bankName: "",
      branchName: "",
    });

    if (isBankVerified) {
      setIsBankVerified(false);
      setBankVerificationId(null);
    }

    // Valid IFSC format
    if (/^[A-Z]{4}0[A-Z0-9]{6}$/.test(val)) {
      setIfscVerifying(true);

      try {
        const res = await dispatch(
          verifyIfsc({
            ifscCode: val,
            ifsc: val,
          })
        );

        const bank =
          res?.payload?.bankName ||
          res?.payload?.BANK ||
          res?.payload?.response?.bankName ||
          res?.payload?.response?.BANK ||
          res?.payload?.data?.bankName ||
          res?.payload?.data?.BANK ||
          "";

        const branch =
          res?.payload?.branchName ||
          res?.payload?.BRANCH ||
          res?.payload?.response?.branchName ||
          res?.payload?.response?.BRANCH ||
          res?.payload?.data?.branchName ||
          res?.payload?.data?.BRANCH ||
          "";

        if (bank || branch) {
          // Automatically fill Bank and Branch fields
          form.setFieldsValue({
            bankName: bank,
            branchName: branch,
          });
        } else {
          form.setFieldsValue({
            bankName: "",
            branchName: "",
          });

          message.warning("Bank details not found for this IFSC code.");
        }
      } catch (error) {
        form.setFieldsValue({
          bankName: "",
          branchName: "",
        });

        message.error("Unable to verify IFSC code.");
      } finally {
        setIfscVerifying(false);
      }
    }
  };

  /**
   * Submit Supplier
   */
  const handleFinish = async (values) => {
    // Duplicate Email and Phone checks
    const cleanEmail = normalizeEmail(values.pocEmail);
    const cleanMobile = normalizePhone(values.pocMobile);

    // Duplicate check against registered business email & phone
    if (registeredBusinessEmails.includes(cleanEmail)) {
      form.setFields([
        {
          name: "pocEmail",
          errors: [
            "Supplier email address cannot be the same as the registered business email address.",
          ],
        },
      ]);
      message.error(
        "Supplier email address cannot be the same as the registered business email address."
      );
      return;
    }

    if (registeredBusinessPhones.includes(cleanMobile)) {
      form.setFields([
        {
          name: "pocMobile",
          errors: [
            "Supplier mobile number cannot be the same as the registered business mobile number.",
          ],
        },
      ]);
      message.error(
        "Supplier mobile number cannot be the same as the registered business mobile number."
      );
      return;
    }

    const isDuplicateEmail = suppliers.some(
      (s) => normalizeEmail(s.pocEmail) === cleanEmail
    );
    if (isDuplicateEmail) {
      form.setFields([
        {
          name: "pocEmail",
          errors: ["Email address already exists for another supplier."],
        },
      ]);
      message.error("Email address already exists for another supplier.");
      return;
    }

    const isDuplicateMobile = suppliers.some(
      (s) => normalizePhone(s.pocMobile) === cleanMobile
    );
    if (isDuplicateMobile) {
      form.setFields([
        {
          name: "pocMobile",
          errors: ["Phone number already exists for another supplier."],
        },
      ]);
      message.error("Phone number already exists for another supplier.");
      return;
    }

    if (!isAllVerified) {
      if (!isPanVerified) {
        message.error("Please verify PAN before adding supplier.");
      } else if (!isGstinVerified) {
        message.error("Please verify GSTIN before adding supplier.");
      } else if (!isBankVerified) {
        message.error("Please verify Bank Account before adding supplier.");
      }
      return;
    }

    const payload = {
      supplierName: values.supplierName?.trim(),

      pocName: values.pocName?.trim(),

      pocMobile: values.pocMobile?.trim(),

      pocEmail: values.pocEmail?.toLowerCase().trim(),

      pan: values.pan?.toUpperCase().trim(),

      gstin: values.gstin?.toUpperCase().trim(),

      accNum: values.accNum?.trim(),

      accHolderName: values.accHolderName?.trim(),

      ifsc: values.ifsc?.toUpperCase().trim(),

      bankName: values.bankName?.trim(),

      branchName: values.branchName?.trim(),

      isBankVerified: isBankVerified,

      bankVerificationId: bankVerificationId || undefined,
    };

    const res = await dispatch(createSupplier(payload));

    if (
      res?.meta?.requestStatus === "fulfilled" &&
      (
        res?.payload?.statusCode === 201 ||
        res?.payload?.data?.msg ||
        res?.payload?.msg
      )
    ) {
      message.success(
        `Supplier "${payload.supplierName}" onboarded successfully.`
      );

      form.resetFields();
      setIsPanVerified(false);
      setIsGstinVerified(false);
      setIsBankVerified(false);
      setBankVerificationId(null);

      onSuccess?.();
      onClose();
    } else {
      const errMsg =
        res?.payload?.response?.data?.error ||
        res?.payload?.response?.data?.message ||
        res?.payload?.error ||
        res?.payload?.message ||
        res?.payload ||
        "Failed to onboard supplier.";

      const errText =
        typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg);

      if (errText.toLowerCase().includes("email")) {
        form.setFields([
          {
            name: "pocEmail",
            errors: [errText],
          },
        ]);
      } else if (
        errText.toLowerCase().includes("phone") ||
        errText.toLowerCase().includes("mobile")
      ) {
        form.setFields([
          {
            name: "pocMobile",
            errors: [errText],
          },
        ]);
      }

      message.error(errText);
    }
  };

  /**
   * Cancel modal
   */
  const handleCancel = () => {
    form.resetFields();
    setIfscVerifying(false);
    setIsPanVerified(false);
    setIsGstinVerified(false);
    setIsBankVerified(false);
    setBankVerificationId(null);
    onClose();
  };

  return (
    <CustomModal
      title="Add New Supplier"
      open={open}
      centered
      onCancel={handleCancel}
      width={720}
      content={
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{
            countryCode: "+91",
          }}
          style={{
            paddingTop: 12,
          }}
        >
          {/* Submit Error */}
          {submitError && (
            <Alert
              type="error"
              message={
                typeof submitError === "string"
                  ? submitError
                  : JSON.stringify(submitError)
              }
              showIcon
              style={{
                marginBottom: 16,
              }}
            />
          )}

          <Row gutter={[16, 0]}>
            {/* Supplier Name */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Supplier Name"
                name="supplierName"
                placeholder="Enter Supplier Name"
                isRequired
                rules={companyNameRules("Supplier")}
                disabled={isPanVerified}
              />
            </Col>

            {/* POC Name */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Contact Person (POC Name)"
                name="pocName"
                placeholder="Enter POC Name"
                isRequired
                rules={nameRules("POC")}
              />
            </Col>

            {/* POC Mobile */}
            <Col xs={24} md={12}>
              <CustomInput
                label="POC Mobile Number"
                name="pocMobile"
                placeholder="Enter 10-digit mobile number"
                addonBefore="+91"
                isRequired
                rules={phoneValidationRules}
              />
            </Col>

            {/* POC Email */}
            <Col xs={24} md={12}>
              <CustomInput
                label="POC Email"
                name="pocEmail"
                type="email"
                placeholder="Enter POC email address"
                isRequired
                rules={emailValidationRules}
              />
            </Col>

            {/* PAN */}
            <Col xs={24} md={12}>
              <InputWithButton
                label="PAN Number"
                name="pan"
                placeholder="ABCDE1234F"
                isRequired
                isUpperCase
                maxLength={10}
                rules={panRules}
                verifyBtn={true}
                isVerified={isPanVerified}
                verifyLoading={panVerifying}
                disableVerify={
                  !/[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(
                    (panValue || "").toUpperCase().trim()
                  ) || panVerifying
                }
                handleVerify={handleVerifyPan}
                onChange={() => {
                  if (isPanVerified) setIsPanVerified(false);
                  if (isGstinVerified) setIsGstinVerified(false);
                }}
              />
            </Col>

            {/* GSTIN */}
            <Col xs={24} md={12}>
              <InputWithButton
                label="GSTIN Number"
                name="gstin"
                placeholder="27ABCDE1234F1Z5"
                isRequired
                isUpperCase
                maxLength={15}
                rules={gstinRules}
                verifyBtn={true}
                isVerified={isGstinVerified}
                verifyLoading={gstinVerifying}
                disableVerify={
                  !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
                    (gstinValue || "").toUpperCase().trim()
                  ) || gstinVerifying
                }
                handleVerify={handleVerifyGstin}
                onChange={() => {
                  if (isGstinVerified) setIsGstinVerified(false);
                }}
              />
            </Col>

            {/* IFSC */}
            <Col xs={24} md={12}>
              <CustomInput
                label="IFSC Code"
                name="ifsc"
                placeholder="e.g. HDFC0001234"
                isRequired
                isUpperCase
                maxLength={11}
                onChange={handleIfscChange}
                rules={ifscRules}
              />

              {/* IFSC Verification Message */}
              {ifscVerifying && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#1890ff",
                    marginTop: -12,
                    marginBottom: 8,
                  }}
                >
                  Verifying IFSC code...
                </div>
              )}
            </Col>

            {/* Bank Name */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Bank Name"
                name="bankName"
                placeholder="Bank Name"
                disabled
              />
            </Col>

            {/* Branch Name */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Branch Name"
                name="branchName"
                placeholder="Branch Name"
                disabled
              />
            </Col>
            {/* Account Holder */}
            <Col xs={24} md={12}>
              <CustomInput
                label="Account Holder Name"
                name="accHolderName"
                placeholder="Enter Account Holder Name"
                isRequired
                rules={nameRules("Account Holder")}
                disabled={isPanVerified}
                onChange={() => {
                  if (isBankVerified) {
                    setIsBankVerified(false);
                    setBankVerificationId(null);
                  }
                }}
              />
            </Col>

            {/* Account Number */}
            <Col xs={24} md={12}>
              <InputWithButton
                label="Bank Account Number"
                name="accNum"
                placeholder="Enter Account Number"
                isRequired
                rules={accNumberRules}
                verifyBtn={true}
                isVerified={isBankVerified}
                verifyLoading={bankVerifying}
                disableVerify={
                  String(accNumValue || "").length < 8 ||
                  String(accNumValue || "").length > 18 ||
                  !ifscValue ||
                  !accHolderNameValue ||
                  bankVerifying
                }
                handleVerify={handleVerifyBank}
                onChange={() => {
                  if (isBankVerified) {
                    setIsBankVerified(false);
                    setBankVerificationId(null);
                  }
                }}
              />
            </Col>


          </Row>

          {/* Buttons */}
          <Row
            justify="end"
            gutter={12}
            style={{
              marginTop: 24,
            }}
          >
            {/* Cancel */}
            <Col>
              <CustomButton
                onClick={handleCancel}
                disabled={submitLoading}
              >
                Cancel
              </CustomButton>
            </Col>

            {/* Submit */}
            <Col>
              <CustomButton
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                disabled={!isAllVerified || submitLoading}
                style={{
                  background: isAllVerified ? "#294DD8" : undefined,
                  minWidth: 120,
                }}
              >
                Add Supplier
              </CustomButton>
            </Col>
          </Row>
        </Form>
      }
    />
  );
};

export default NewSupplierModal;